/**
 * Hooke's law and the ideal spring, in SI units.
 *
 *   F = k x                    force needed to stretch (x > 0) or compress (x < 0) a spring
 *   U = ½ k x²                 elastic potential energy stored (= the area under F against x)
 *   1/kₛ = Σ 1/kᵢ              springs in series: same force, extensions add
 *   kₚ = Σ kᵢ                  springs in parallel: same extension, forces add
 *   T = 2π √(m / k)            mass on a spring: independent of amplitude
 *
 * `F` here is the *applied* force; the spring's restoring force is −F. The model is a
 * massless spring within its elastic limit.
 */

export type SpringError =
  | "missing"
  | "non-positive"
  | "zero-extension"
  | "sign-mismatch"
  | "not-finite";

export type HookeTarget = "force" | "stiffness" | "extension";

export interface HookeInputs {
  force: number | null;
  stiffness: number | null;
  extension: number | null;
}

export interface HookeSolution {
  force: number;
  stiffness: number;
  extension: number;
  /** Elastic potential energy ½kx², J. */
  energy: number;
}

interface Failure {
  ok: false;
  error: SpringError;
}

const NEEDS: Record<HookeTarget, readonly (keyof HookeInputs)[]> = {
  force: ["stiffness", "extension"],
  stiffness: ["force", "extension"],
  extension: ["force", "stiffness"],
};

function finite(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

export function solveHooke(
  inputs: HookeInputs,
  target: HookeTarget
): { ok: true; value: HookeSolution } | Failure {
  for (const key of NEEDS[target]) {
    if (!finite(inputs[key])) {
      return { ok: false, error: "missing" };
    }
  }
  const input = (key: keyof HookeInputs) => inputs[key] ?? 0;
  let { force, stiffness, extension } = {
    force: input("force"),
    stiffness: input("stiffness"),
    extension: input("extension"),
  };
  if (target !== "stiffness" && stiffness <= 0) {
    return { ok: false, error: "non-positive" };
  }
  if (target === "force") {
    force = stiffness * extension;
  } else if (target === "extension") {
    extension = force / stiffness;
  } else {
    if (extension === 0) {
      return { ok: false, error: "zero-extension" };
    }
    stiffness = force / extension;
    if (stiffness <= 0) {
      return {
        ok: false,
        error: stiffness === 0 ? "non-positive" : "sign-mismatch",
      };
    }
  }
  const solution = {
    force,
    stiffness,
    extension,
    energy: 0.5 * stiffness * extension ** 2,
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── Combinations ─────────────────────────────────────────────────────────── */

export type Arrangement = "series" | "parallel";

export interface ComboRow {
  stiffness: number;
  /** Force carried by this spring, N. */
  force: number;
  /** Extension of this spring, m. */
  extension: number;
  /** Energy stored in this spring, J. */
  energy: number;
}

export interface ComboSolution {
  stiffness: number;
  rows: ComboRow[];
  /** Total extension of the combination under the load, m (null without a load). */
  extension: number | null;
  energy: number | null;
  load: number | null;
}

export function seriesStiffness(values: readonly number[]): number {
  return 1 / values.reduce((sum, k) => sum + 1 / k, 0);
}

export function parallelStiffness(values: readonly number[]): number {
  return values.reduce((sum, k) => sum + k, 0);
}

/** Equivalent stiffness of springs, and how a load divides among them. */
export function solveCombination(
  values: readonly (number | null)[],
  arrangement: Arrangement,
  load: number | null
): { ok: true; value: ComboSolution } | Failure {
  if (values.length === 0 || values.some((k) => !finite(k))) {
    return { ok: false, error: "missing" };
  }
  const ks = values as number[];
  if (ks.some((k) => k <= 0)) {
    return { ok: false, error: "non-positive" };
  }
  const stiffness =
    arrangement === "series" ? seriesStiffness(ks) : parallelStiffness(ks);
  if (!Number.isFinite(stiffness) || stiffness <= 0) {
    return { ok: false, error: "not-finite" };
  }
  const totalExtension = load === null ? null : load / stiffness;
  const rows = ks.map((k) => {
    // Series: every spring carries the whole load. Parallel: every spring has the
    // whole extension.
    const force =
      arrangement === "series" ? (load ?? 0) : k * ((load ?? 0) / stiffness);
    const extension = force / k;
    return { stiffness: k, force, extension, energy: 0.5 * k * extension ** 2 };
  });
  return {
    ok: true,
    value: {
      stiffness,
      rows,
      extension: totalExtension,
      energy: load === null ? null : rows.reduce((s, r) => s + r.energy, 0),
      load,
    },
  };
}

/* ── Mass on a spring ─────────────────────────────────────────────────────── */

export type OscillatorTarget = "period" | "mass" | "stiffness";

export interface OscillatorInputs {
  mass: number | null;
  stiffness: number | null;
  period: number | null;
  /** Amplitude, m — optional. */
  amplitude: number | null;
}

export interface OscillatorSolution {
  mass: number;
  stiffness: number;
  period: number;
  frequency: number;
  /** Angular frequency ω = √(k/m), rad/s. */
  angularFrequency: number;
  /** Largest speed Aω, m/s — null without an amplitude. */
  maxSpeed: number | null;
  /** Largest acceleration Aω², m/s². */
  maxAcceleration: number | null;
  /** Total mechanical energy ½kA², J. */
  energy: number | null;
  amplitude: number | null;
}

const OSC_NEEDS: Record<OscillatorTarget, readonly (keyof OscillatorInputs)[]> =
  {
    period: ["mass", "stiffness"],
    mass: ["period", "stiffness"],
    stiffness: ["period", "mass"],
  };

export function solveOscillator(
  inputs: OscillatorInputs,
  target: OscillatorTarget
): { ok: true; value: OscillatorSolution } | Failure {
  for (const key of OSC_NEEDS[target]) {
    if (inputs[key] === null) {
      return { ok: false, error: "missing" };
    }
    if (!((inputs[key] as number) > 0 && Number.isFinite(inputs[key]))) {
      return { ok: false, error: "non-positive" };
    }
  }
  const amplitude = inputs.amplitude;
  if (amplitude !== null && !(amplitude >= 0 && Number.isFinite(amplitude))) {
    return { ok: false, error: "non-positive" };
  }
  const twoPi = 2 * Math.PI;
  let { mass, stiffness, period } = {
    mass: inputs.mass ?? 0,
    stiffness: inputs.stiffness ?? 0,
    period: inputs.period ?? 0,
  };
  if (target === "period") {
    period = twoPi * Math.sqrt(mass / stiffness);
  } else if (target === "mass") {
    mass = (stiffness * period ** 2) / twoPi ** 2;
  } else {
    stiffness = (twoPi ** 2 * mass) / period ** 2;
  }
  const omega = twoPi / period;
  const solution: OscillatorSolution = {
    mass,
    stiffness,
    period,
    frequency: 1 / period,
    angularFrequency: omega,
    maxSpeed: amplitude === null ? null : amplitude * omega,
    maxAcceleration: amplitude === null ? null : amplitude * omega ** 2,
    energy: amplitude === null ? null : 0.5 * stiffness * amplitude ** 2,
    amplitude,
  };
  const numbers = Object.values(solution).filter(
    (item): item is number => typeof item === "number"
  );
  if (!numbers.every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}
